import { getState, subscribe, updateRouteFromHash } from '../state';
import { renderCompareView } from './compare-view';
import { renderHome } from './home';
import { renderPresetView } from './preset-view';
import { renderComponentView } from './component-view';

export interface RouteInfo {
  name: 'home' | 'component' | 'compare' | 'preset' | 'notfound';
  params: Record<string, string>;
  query: Record<string, string>;
}

export function parseRoute(hash: string): RouteInfo {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart, queryPart] = raw.split('?');
  const query: Record<string, string> = {};
  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    params.forEach((value, key) => {
      query[key] = value;
    });
  }

  const parts = pathPart.split('/').filter(Boolean);
  if (parts.length === 0) {
    return { name: 'home', params: {}, query };
  }

  if (parts[0] === 'component' && parts[1]) {
    return { name: 'component', params: { id: parts[1] }, query };
  }

  if (parts[0] === 'compare' && parts[1] && parts[2]) {
    return { name: 'compare', params: { idA: parts[1], idB: parts[2] }, query };
  }

  if (parts[0] === 'preset') {
    return { name: 'preset', params: {}, query };
  }

  return { name: 'notfound', params: {}, query };
}

const FADE_MS = 350;

export function initRouter(root: HTMLElement): void {
  // Persistent transition — never removed
  root.style.transition = `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`;
  root.style.opacity = '1';
  root.style.transform = 'translateY(0)';

  let isFirst = true;

  function renderInto(): void {
    const route = parseRoute(getState().route);
    root.innerHTML = '';

    if (route.name === 'home') { renderHome(root); return; }
    if (route.name === 'component') { renderComponentView(root, route.params.id, route.query); return; }
    if (route.name === 'compare') { renderCompareView(root, route.params.idA, route.params.idB); return; }
    if (route.name === 'preset') { renderPresetView(root); return; }
    renderNotFound(root);
  }

  function render(): void {
    if (isFirst) {
      isFirst = false;
      renderInto();
      return;
    }

    // Fade out
    root.style.opacity = '0';
    root.style.transform = 'translateY(10px)';

    setTimeout(() => {
      // Swap content while invisible
      renderInto();

      // Force browser to register the new DOM before fading in
      requestAnimationFrame(() => {
        root.style.opacity = '1';
        root.style.transform = 'translateY(0)';
      });
    }, FADE_MS);
  }

  subscribe(render);
  updateRouteFromHash();
}

function renderNotFound(root: HTMLElement): void {
  root.innerHTML = `
    <div class="not-found">
      <h1>404</h1>
      <p>Page not found</p>
      <a href="#/">Back home</a>
    </div>
  `;
}
